import type { FarmDto, FarmResumeDto, FarmsDto } from "@farm/data/dto/farm.dto";
import {
    FarmDtoSchema,
    FarmResumeDtoSchema,
    FarmsDtoSchema,
} from "@farm/data/dto/farm.dto";
import type {
    FarmCreate,
    FarmCreateInputProps,
    FarmUpdate,
} from "@farm/domain/models/farm.model";
import {
    Farm,
    FarmCreateSchema,
    FarmResume,
    Farms,
    FarmUpdateSchema,
} from "@farm/domain/models/farm.model";
import type { ModelError } from "@iot-platform/core";
import { NonRecuperableModelError } from "@iot-platform/core";
import { injectable } from "inversify";
import { err, ok, Result } from "neverthrow";
import type * as z from "zod";
// eslint-disable-next-line boundaries/dependencies
import type { FarmModalFormValuesValidated } from "@farm/presentation/components/modal/farm/farm-modal-form.view";

@injectable()
export class FarmMapper {
    mapResume(
        farmDto: FarmResumeDto,
    ): Result<FarmResume, ModelError<typeof FarmResume>> {
        const parsed = FarmResumeDtoSchema.safeParse(farmDto);
        if (!parsed.success) {
            console.error(parsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return FarmResume.createWithRecovery({
            id: parsed.data.id,
            name: parsed.data.name,
        });
    }

    mapList(farmsDto: FarmsDto): Farms {
        const parsed = FarmsDtoSchema.safeParse(farmsDto);
        if (!parsed.success) {
            console.error(parsed.error.issues);
            return Farms.empty();
        }

        const farms = farmsDto
            .map((dto) =>
                this.mapResume(dto).match(
                    (farm) => farm,
                    (error) => {
                        if (!error.recuperable) {
                            console.error(
                                "Non-recoverable error mapping farms:",
                                error,
                            );
                        }
                        return null;
                    },
                ),
            )
            .filter((farm): farm is FarmResume => farm !== null);

        const farmsResult = Farms.create(farms);

        return farmsResult.match(
            (list) => list,
            (error) => {
                console.error("Error creating Farms:", error);
                return Farms.empty();
            },
        );
    }

    mapDetail(farmDto: FarmDto): Result<Farm, ModelError<typeof Farm>> {
        const parsed = FarmDtoSchema.safeParse(farmDto);
        if (!parsed.success) {
            console.error(parsed.error.issues);
            return err(new NonRecuperableModelError());
        }

        return Farm.create({
            id: parsed.data.id,
            name: parsed.data.name,
            enabledSatellites: parsed.data.enabled_satellites,
            plots: parsed.data.parcels.map((parcel) => ({
                id: parcel.id,
                geoJson: parcel.geo_json,
                name: parcel.name,
                documentUrl: parcel.document_url,
                clayPercentage: parcel.clay_percentage,
                siltPercentage: parcel.silt_percentage,
                sandPercentage: parcel.sand_percentage,
                stations: parcel.stations.map((station) => ({
                    id: station.id,
                    name: station.name,
                    code: station.code,
                })),
            })),
            users: parsed.data.users.map((user) => ({
                id: user.id,
                email: user.email,
            })),
        }).mapErr(() => new NonRecuperableModelError());
    }

    mapFormToCreate(
        farm: FarmModalFormValuesValidated,
    ): Result<FarmCreate, z.ZodError<FarmCreate>> {
        const maybeFarmCreate = FarmCreateSchema.safeParse({
            name: farm.name,
            enabledSatellites: farm.enabledSatellites,
            userIds: farm.userIds,
        } satisfies FarmCreateInputProps);

        return maybeFarmCreate.success
            ? ok(maybeFarmCreate.data)
            : err(maybeFarmCreate.error);
    }

    mapFormToEdit(
        id: number,
        farm: FarmModalFormValuesValidated,
    ): Result<FarmUpdate, z.ZodError<FarmUpdate>> {
        const maybeFarmUpdate = FarmUpdateSchema.safeParse({
            id,
            name: farm.name,
            enabledSatellites: farm.enabledSatellites,
            userIds: farm.userIds,
        } satisfies FarmUpdate);

        return maybeFarmUpdate.success
            ? ok(maybeFarmUpdate.data)
            : err(maybeFarmUpdate.error);
    }
}
